import { computed, ref, unref, nextTick } from 'vue';
import { defineStore, storeToRefs } from 'pinia';

import { useChatbotFlow } from '@/library/cf-v1';
import { RequestLimitModalChoice, useConnectToAgentWizard } from '@/library/cf-v1/ConnectRequestEvent';
import * as Model from '@/common/api/models.pleased';
import { getLiveChatQueueMessages } from '@/common/bot/flow-frameworks/messages';
import { useSupportWizardMessaging } from '@/library/cf-v1/useSupportWizardMessaging';
import { InteractionView, useChatWidget } from './useChatWidget';
import { useEventLogger } from '@/composables/useEventLogger';
import { useRouter } from 'vue-router/composables';
import { Bridge } from '@/common/bridge';
import { parseErrorMessage } from '@/common/error';
import { analytics, CRITICAL_EVENTS } from '@/common/logs';
import { useAtomic } from '@/use/useAtomic';
import { useOpenChatsQuery } from '@/library/cf-v1/useOpenChatsQuery';
import { useResumeAiChat } from '@/library/cf-v1/ai-service/useResumeAiChat';
import { useBotUser } from './useBotUser';
import { useConnectConfig } from '@/stores/useConnectConfig';
import { useVoiceTranscript } from '@/stores/useVoiceTranscript';
import { useVoiceAiHandover } from '@/stores/useVoiceAiHandover';
import { isLandingEntrySupported, resolveLandingEntryUserId } from '@/common/landingEntry.utils';
import * as ItemType from '@/common/bot/lib/ItemType';
import { useBrandConfig } from '@/stores/useBrandConfig';
import { resolveVoiceAiApiErrorMessage } from '@/common/voice-ai/voiceAi.errors';
import { sendQueuedLiveChatMessages } from './sendQueuedLiveChatMessages';
import { noop } from '@vueuse/core';
import { devLog } from '@/common/debug';
import { logger as globalLogger } from '@/common/api/logger';

export const useWidgetConversation = defineStore('widgetConversation', () => {
  const {
    conversation,

    startFlow,
    startFlowRunning,
    submitFlowAction,
    submitFlowExternalAction,

    enterLandingCollapsedState,
    showLandingChannelPicker,
    enterSkipToLiveAgentFlow,
    startLandingAiVoiceCall,
    onLandingAiVoiceStart,
    onLandingFlow,

    onEndChat,
    onRestartChat,
    onConnectSupport,
    onTransactionSelected,
    onEditSelectedTransaction,
    onAbortCancelRequest,
    onConnectOptionSelected,

    onEndLiveChat,
    onEndVoiceCall,
    onResumeChat,
    onVoiceAiUnavailable,
    onSelectedEntityFlow,
    onFailedKycFlow,

    aiFlow,
    checkCanConnectToAI,
    submitUserMessageForAI,
    onAiIntervention,
    onAiFlowStart,
    onEndAiChat,
    onResumeAiChat,

    // Mocked flow
    mockedFlow,
    onMockedFlow,

    flowStatus,
    resetFlow,
  } = useChatbotFlow();

  const logger = useEventLogger();
  const router = useRouter();

  // TODO: find a better place for this
  const feedbackState = ref<{
    id?: number | string;
    type?: Model.ChatRateMode;
  }>({});

  const supportWizard = useConnectToAgentWizard(conversation);
  const userStore = useBotUser();
  const { isAuthenticated, user } = storeToRefs(userStore);
  const connectConfigStore = useConnectConfig();
  const brandConfigStore = useBrandConfig();
  const { fetchBrandConfig } = brandConfigStore;
  const { isSkipToLiveAgentActive } = storeToRefs(brandConfigStore);
  const chatWidgetStore = useChatWidget();
  const { setIsChatOverlayVisible, setInteractionView } = chatWidgetStore;
  const { interactionView } = storeToRefs(chatWidgetStore);
  const { markSessionAsEnded } = useResumeAiChat();
  const { invalidate } = useOpenChatsQuery();
  const voiceTranscript = useVoiceTranscript();
  const voiceAiHandover = useVoiceAiHandover();

  const getLandingEntryUserId = () => resolveLandingEntryUserId(isAuthenticated.value, user.value.id);

  /** True once the user has picked a connect channel (live chat, voice, or open request). */
  const hasSelectedConnectChannel = () => {
    return conversation.messages.value.some(
      (message) => ItemType.isConnectOptionSelectMessage(message) && message.selectedOption
    );
  };

  const showSkipToLiveAgentEntryIfActive = async (): Promise<boolean> => {
    // Overlay can open before startFlow() finishes; await shared in-flight fetch.
    await fetchBrandConfig();

    if (!isSkipToLiveAgentActive.value) {
      return false;
    }

    // openLandingOverlay / resetLandingEntryPoint can run while the connect wizard is already
    // in progress (e.g. user closed the overlay after choosing a channel, then reopened).
    // Do not restart skip flow in that case — not related to P1 live-chat/AI resume on init.
    if (hasSelectedConnectChannel()) {
      return false;
    }

    return enterSkipToLiveAgentFlow();
  };

  const resetLandingEntryPoint = async () => {
    conversation.clear({ __CLEARING_CONVERSATION: true });

    if (await showSkipToLiveAgentEntryIfActive()) {
      return;
    }

    showLandingChannelPicker();
  };

  // Handlers for default flow (no AI)
  // Initial start connect support from default flow
  onConnectSupport(({ connectToAgentConfig }) => {
    if (connectToAgentConfig.landingOpenRequest || connectToAgentConfig.skipToLiveAgent) {
      connectConfigStore.setConnectConfig(connectToAgentConfig);
    }

    supportWizard.startWizard({ connectToAgentConfig });
  });
  onTransactionSelected(supportWizard.submitTransactionSelected);
  onEditSelectedTransaction(supportWizard.editSelectedTransaction);
  // Cancel must not clearHandoff — pending id is needed for a later Contact Support create.
  onConnectOptionSelected(supportWizard.submitSelectedConnectOption);
  onAbortCancelRequest(supportWizard.handleAbortCancelRequest);

  // Handle AI chat intervention
  onAiIntervention(supportWizard.handleIntervention);

  // Handle resume live chat
  onResumeChat(supportWizard.handleResumeChat);

  onVoiceAiUnavailable(() => {
    supportWizard.openVoiceAiUnavailableDialog(resetLandingEntryPoint);
  });

  const showVoiceAiNewChatError = (error: unknown) => {
    supportWizard.openVoiceAiNewChatErrorDialog({
      message: resolveVoiceAiApiErrorMessage(error),
      onDismiss: resetLandingEntryPoint,
    });
  };

  // Handle resume AI chat
  onResumeAiChat(() => {
    setIsChatOverlayVisible(true);
  });

  // Handle selected entity flow
  onSelectedEntityFlow(() => {
    setIsChatOverlayVisible(true);
  });

  // Handle failed KYC flow
  onFailedKycFlow(() => {
    setIsChatOverlayVisible(true);
  });

  // Handle mocked flow - log when mocked flow is triggered
  onMockedFlow(() => {
    devLog.debug('[WIDGET_CONVERSATION] Mocked flow triggered', {
      registryId: mockedFlow.mockedFlowConfig?.value?.registryId,
    });
  });

  onLandingFlow(() => {
    enterLandingCollapsedState();
  });

  const runLandingOverlayEntry = async () => {
    if (await showSkipToLiveAgentEntryIfActive()) {
      return;
    }

    if (!isLandingEntrySupported({ userId: getLandingEntryUserId() })) {
      return;
    }

    if (interactionView.value === InteractionView.LANDING) {
      showLandingChannelPicker();
    }
  };

  function openLandingOverlay() {
    setIsChatOverlayVisible(true);

    // Defer until overlay/view updates settle; only show picker from collapsed landing, not mid-conversation.
    nextTick(() => {
      runLandingOverlayEntry().catch(noop);
    });
  }

  const wizardMessaging = useSupportWizardMessaging({
    conversation,
    // Regular OPEN_REQUEST confirmation reads the ticket the FE just created. The Voice AI
    // handoff OPEN_REQUEST (which creates no ticket) carries its handoff ticket id on the
    // connect event payload instead, so no cross-store fallback is needed here.
    ticketId: computed(() => (supportWizard.support.ticket.ticket as unknown as Model.TicketDetail)?.id ?? undefined),
  });

  // Live chat: flush queue in order, preserve per-line createdAt (see Chatroom.sendMessagesToAgentChat)
  supportWizard.support.chat.onChat(({ chat }) => {
    devLog.debug('[WIDGET_CONVERSATION] Live chat started', {
      support: { ...supportWizard.support.chat },
    });
    const messages = [...conversation.messages.value];
    const selections = getLiveChatQueueMessages(messages);

    sendQueuedLiveChatMessages(selections, chat).catch((err) => {
      globalLogger.error('[WIDGET_CONVERSATION] sendQueuedLiveChatMessages', { error: parseErrorMessage(err) });
      analytics.trackCriticalEvent({
        name: CRITICAL_EVENTS.LIVE_CHAT_QUEUE_SEND_FAILED,
        metadata: {
          error: parseErrorMessage(err),
        },
      });
    });
  });

  supportWizard.onWizardEvent(wizardMessaging.listenWizardEvents);

  function reset() {
    devLog.debug('[WIDGET_CONVERSATION] Resetting conversation', {
      support: { ...supportWizard.support.chat },
    });
    resetFlow();
    supportWizard.reset();
    devLog.debug('[WIDGET_CONVERSATION] Widget conversation reset', {
      support: { ...supportWizard.support.chat },
    });
  }

  const [endPleasedChat] = useAtomic(async () => {
    const chat = unref(supportWizard.support.chat.chat);
    devLog.debug('[WIDGET_CONVERSATION] Ending pleased chat', {
      chat: { ...chat },
      support: { ...supportWizard.support.chat },
    });
    if (chat) {
      // There is a pleased chat ongoing, show them the rating page, if the someone
      // had joined the chat.
      // Make sure that the chat has been closed.
      devLog.debug('[WIDGET_CONVERSATION] Ending pleased chat passed check', {
        chat: { ...chat },
        support: { ...supportWizard.support.chat },
      });
      await chat.end();

      devLog.debug('[WIDGET_CONVERSATION] Pleased chat ended', {
        chat: { ...chat },
        support: { ...supportWizard.support.chat },
      });

      if (chat.didAgentJoin) {
        devLog.debug('[WIDGET_CONVERSATION] Chat ended and agent joined', {
          chat: { ...chat },
          support: { ...supportWizard.support.chat },
        });

        feedbackState.value = {
          id: chat.id,
          type: 'chat',
        };

        setInteractionView(InteractionView.CSAT);
        return true;
      }
    }

    if (aiFlow.value?.active || aiFlow.value?.showEndChat) {
      markSessionAsEnded(aiFlow.value?.sessionId || '');

      // If the AI flow is active, we want to show the feedback page.
      feedbackState.value = {
        id: aiFlow.value?.sessionId || '',
        type: 'AI',
      };
      setInteractionView(InteractionView.CSAT);
      return true;
    }
  });

  /**
   * Resets the flow and shows the landing channel picker (`NONE` + connect message) when supported.
   * Call `enterLandingCollapsedState()` after this when hiding the overlay.
   */
  async function restartConversation(options: { enterSkipFlow?: boolean } = {}) {
    const shouldEnterSkipFlow = options.enterSkipFlow ?? true;

    reset();
    await startFlow();

    if (isSkipToLiveAgentActive.value) {
      await nextTick();

      if (shouldEnterSkipFlow) {
        enterSkipToLiveAgentFlow();
      }

      await logger.syncLogs();
      return;
    }

    if (isLandingEntrySupported({ userId: getLandingEntryUserId() })) {
      // Defer until view updates settle; show channel picker after restart with overlay open.
      await nextTick();
      showLandingChannelPicker();
    } else {
      const isAiEligible = checkCanConnectToAI();
      setInteractionView(isAiEligible ? InteractionView.INPUT : InteractionView.NONE);
    }

    await logger.syncLogs();
  }

  async function closeChatbot() {
    voiceTranscript.reset();
    voiceAiHandover.clearHandoff();
    await restartConversation({ enterSkipFlow: false });
    if (isLandingEntrySupported({ userId: getLandingEntryUserId() })) {
      enterLandingCollapsedState();
    } else {
      setInteractionView(InteractionView.INPUT);
    }
    setIsChatOverlayVisible(false);
    router.replace('/new-home').catch(noop);
    Bridge.sendToParent('CLOSE');
  }

  async function endChatWithoutNavigation(): Promise<boolean> {
    const route = await endPleasedChat();
    await invalidate();
    devLog.debug('[WIDGET_CONVERSATION] Ending pleased chat without navigation');
    if (route) {
      return true;
    }

    await restartConversation({ enterSkipFlow: false });
    if (isLandingEntrySupported({ userId: getLandingEntryUserId() })) {
      enterLandingCollapsedState();
    }
    setIsChatOverlayVisible(false);
    return false;
  }

  async function endChat() {
    voiceTranscript.reset();
    voiceAiHandover.clearHandoff();
    const earlyExit = await endChatWithoutNavigation();
    devLog.debug('[WIDGET_CONVERSATION] Ending chat', {
      earlyExit,
    });
    if (earlyExit) {
      return;
    }

    router.replace('/new-home');
    Bridge.sendToParent('CLOSE');
  }

  supportWizard.onRequestLimitModalResolved(async (choice) => {
    if (choice === RequestLimitModalChoice.MY_REQUESTS) {
      await endChatWithoutNavigation();
      return;
    }
    await endChat();
  });

  onEndChat(endChat);
  onRestartChat(restartConversation);

  return {
    conversation: { ...conversation },
    feedbackState,

    endChat,
    endChatWithoutNavigation,
    onEndChat,
    startFlow,
    startFlowRunning,
    submitFlowAction,
    submitFlowExternalAction,
    supportWizard: { ...supportWizard },
    flowStatus,

    aiFlow,
    checkCanConnectToAI,
    submitUserMessageForAI,
    onAiFlowStart,

    closeChatbot,
    restartConversation,
    resetFlow,
    reset,
    onAbortCancelRequest,
    enterLandingCollapsedState,
    showLandingChannelPicker,
    enterSkipToLiveAgentFlow,
    startLandingAiVoiceCall,
    onLandingAiVoiceStart,
    openLandingOverlay,
    showVoiceAiNewChatError,

    onEndLiveChat,
    onEndVoiceCall,
    onEndAiChat,

    // Mocked flow
    mockedFlow,
    onMockedFlow,
  };
});
